import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, notification, Popconfirm } from "antd";
import {
    InteractionOutlined,
    CoffeeOutlined,
    CloseCircleFilled,
    StopOutlined,
    StopFilled,
    MailFilled,
} from "@ant-design/icons";
import UserService from "@Services/UserService";
import { MainContext } from "@Context/provider";
import { IConnectionRequestDetails } from "@Models/IUserConnectionDetails";
import SessionManager from "@Core/session";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { GroupContext, SystemContext, QueryKeys, UserContext } from "@Constants";
import InspectorModuleTitle from "@Components/groups/InspectorModuleTitle";
import AccountService from "@Services/AccountService";
import { IUserDetails, IUserPayload } from "@Models/IUserDetails";
import { ICheckCapFull } from "./types";
import { hasCapability } from "@Utils";

interface IProps {
    instance: string;
    checkCap: ICheckCapFull;
}

const ModuleUserActions: React.FC<IProps> = ({ instance, checkCap }: IProps) => {
    const { ui: uiState, setCurrentUser } = useContext(MainContext);
    const [connectionEnabled, setConnectionEnabled] = useState<boolean>(false);
    const [fetching, setFetching] = useState<boolean>(false);
    const [data, setData] = useState<IUserDetails>(null);

    /**
     * Return the currently selected context
     */
    const getCurrentContext = () => {
        if (uiState.currentGroup === null) return SystemContext;
        if (uiState.currentUser === null) return GroupContext;
        return UserContext;
    };

    const queryClient = useQueryClient();
    const allowSuspend: boolean = checkCap("Account/ToggleSuspension");
    const allowDelete: boolean = checkCap("Account/Delete");
    const allowConnect: boolean =
        instance !== SessionManager.getServiceUser()?.userId && checkCap("UserConnection/CreateRequest");
    const allowResend: boolean = data?.status == "invited";
    const canViewPrivate = hasCapability("User/GetPrivate", getCurrentContext());

    const stateRef = useRef<IUserDetails>();
    stateRef.current = data;

    /**
     * Check if the session user is already connected to the selected user.
     */
    const getConnectionEnabled = async () => {
        const service = new UserService();
        const connectionsResult = await service.getUserConnections(SessionManager.getServiceUser()?.userId);
        if (!connectionsResult.hasErrors) {
            if (connectionsResult.value.findIndex((x) => x.userId === instance) === -1) {
                setConnectionEnabled(true);
            }
        }
    };

    /**
     * Send a connection request to this user.
     */
    const sendConnectionRequest = async () => {
        setFetching(true);
        const model: IConnectionRequestDetails = { users: [{ id: instance }] };
        const service = new UserService();
        const sendResult = await service.sendConnectionRequest(SessionManager.getServiceUser()?.userId, model);
        setConnectionEnabled(false);
        if (!sendResult.hasErrors) {
            notification.success({ message: "Connection request sent." });
        } else {
            notification.error({ message: "Error sending connection request." });
        }
        setFetching(false);
    };

    /**
     * Delete the user from the platform.
     */
    const deleteUser = async () => {
        setFetching(true);
        const service = new AccountService();
        await setCurrentUser(null);
        const deleteResult = await service.delete([instance]);
        if (!deleteResult.hasErrors) {
            notification.success({ message: "User account deleted." });
            await queryClient.invalidateQueries([QueryKeys.GroupMembers]);
        } else {
            notification.error({ message: "Error deleting user." });
        }
        setFetching(false);
    };

    /**
     * Suspend the user's account.
     */
    const suspendUser = async () => {
        setFetching(true);
        const service = new AccountService();
        await setCurrentUser(null);
        const toggleSuspensionResult = await service.toggleSuspension([instance], false);
        if (!toggleSuspensionResult.hasErrors) {
            notification.success({ message: "User account suspended." });
            await queryClient.invalidateQueries([QueryKeys.GroupMembers]);
            await queryClient.invalidateQueries([QueryKeys.UserSearch]);
        } else {
            notification.error({ message: "Error suspending user account." });
        }
        setFetching(false);
    };

    /**
     * Unsuspend the user's account.
     */
    const unsuspendUser = async () => {
        setFetching(true);
        const service = new AccountService();
        await setCurrentUser(null);
        const toggleSuspensionResult = await service.toggleSuspension([instance], true);
        if (!toggleSuspensionResult.hasErrors) {
            notification.success({ message: "User account unsuspended." });
            await queryClient.invalidateQueries([QueryKeys.GroupMembers]);
            await queryClient.invalidateQueries([QueryKeys.UserSearch]);
        } else {
            notification.error({ message: "Error unsuspending user account." });
        }
        setFetching(false);
    };

    /**
     * Resend an invite to the user's email.
     */
    const resendUser = async () => {
        setFetching(true);
        const service = new AccountService();
        const resendResult = await service.resendRegistration(data?.userId);
        if (!resendResult.hasErrors) {
            notification.success({ message: "Invitation resent." });
        } else {
            notification.error({ message: "Error sending email invite." });
        }
        setFetching(false);
    };

    /**
     * Fetch connection enabled
     */
    useEffect(() => {
        if (allowConnect) getConnectionEnabled().then();
    }, []);

    /**
     * Fetch current user public data.
     */
    useEffect(() => {
        stateRef.current = null;
        setData(null);

        const fetch = async () => {
            setFetching(true);
            const service = new UserService();
            const user = canViewPrivate
                ? await service.getPrivate([instance], false)
                : await service.getPublic([instance], false, false);
            if (!user.hasErrors) {
                stateRef.current = user.value[0];
                if (stateRef.current !== null) {
                    setData(stateRef.current);
                }
            }
        };

        const query = queryClient.getQueryData<InfiniteData<IUserPayload>>([
            QueryKeys.GroupMembers,
            uiState.currentGroup.instance,
        ]);
        const user = query?.pages
            ?.map((x) => x.users)
            ?.flat()
            ?.find((x) => x.userId === instance);
        if (user !== undefined && user !== null) {
            stateRef.current = user;
            if (stateRef.current !== null) {
                setData(stateRef.current);
            }
        } else {
            fetch().then(() => {
                setFetching(false);
            });
        }
    }, [uiState.currentUser]);

    return (
        (allowDelete || allowConnect || allowSuspend || allowResend) && (
            <>
                <div style={{ padding: "12px 16px" }}>
                    <InspectorModuleTitle
                        title={"Actions"}
                        slug={"actions"}
                        fetching={fetching}
                        icon={<InteractionOutlined />}
                    />
                    {allowResend ? (
                        <Popconfirm
                            placement="top"
                            title="Resend email invitation"
                            okText="Yes, send a new email invite"
                            cancelText="Cancel"
                            className="confirmation"
                            onConfirm={resendUser}
                            cancelButtonProps={{ className: "secondary" }}
                        >
                            <Button
                                style={{ width: "100%" }}
                                icon={<MailFilled />}
                                className="secondary"
                                loading={fetching}
                                title="Resend email invite"
                            >
                                Resend Email Invite
                            </Button>
                        </Popconfirm>
                    ) : null}
                    {allowConnect ? (
                        <>
                            <Button
                                style={{ width: "100%", marginTop: "10px" }}
                                icon={<CoffeeOutlined />}
                                onClick={sendConnectionRequest}
                                className="secondary"
                                disabled={!connectionEnabled}
                                loading={fetching}
                                title="Invite user to connect"
                            >
                                Send Connection Request
                            </Button>
                            <hr />
                        </>
                    ) : null}
                    {allowSuspend && stateRef.current?.status == "active" ? (
                        <Popconfirm
                            placement="top"
                            title="Suspend this user account? The user will lose access until unsuspended by admin."
                            okText="Yes, suspend the user"
                            cancelText="Cancel"
                            className="confirmation"
                            onConfirm={suspendUser}
                            cancelButtonProps={{ className: "secondary" }}
                        >
                            <Button
                                style={{ width: "100%", marginTop: "10px" }}
                                icon={<StopOutlined />}
                                className="secondary danger"
                                loading={fetching}
                                title="Suspend user account"
                            >
                                Suspend User
                            </Button>
                            <hr />
                        </Popconfirm>
                    ) : null}
                    {allowSuspend && stateRef.current?.status == "suspended" ? (
                        <Popconfirm
                            placement="top"
                            title="Unsuspend this user account? The user will regain access to the platform."
                            okText="Yes, make this user active"
                            cancelText="Cancel"
                            className="confirmation"
                            onConfirm={unsuspendUser}
                            cancelButtonProps={{ className: "secondary" }}
                        >
                            <Button
                                style={{ width: "100%", marginTop: "10px" }}
                                icon={<StopFilled />}
                                className="secondary"
                                loading={fetching}
                                title="Unsuspend user account"
                            >
                                Unsuspend User
                            </Button>
                            <hr />
                        </Popconfirm>
                    ) : null}
                    {allowDelete ? (
                        <Popconfirm
                            placement="top"
                            title="Delete this user account completely? This cannot be undone."
                            okText="Yes, delete the user"
                            cancelText="Cancel"
                            className="confirmation"
                            onConfirm={deleteUser}
                            cancelButtonProps={{ className: "secondary" }}
                        >
                            <Button
                                style={{ width: "100%", marginTop: "10px" }}
                                icon={<CloseCircleFilled />}
                                className="secondary"
                                loading={fetching}
                                title="Delete user from platform"
                            >
                                Delete User
                            </Button>
                        </Popconfirm>
                    ) : null}
                </div>
                <hr />
            </>
        )
    );
};
export default ModuleUserActions;
